import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Download, Copy, Check, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import GlobalMetaforaLogo from "@/components/GlobalMetaforaLogo";
import { LogoIcon } from "@/components/LogoWithText";
import { toast } from "sonner";

const LogoDownloadPage = () => {
  const [copied, setCopied] = useState(false);
  const logoRef = useRef(null);

  const downloadSVG = (size, name) => {
    const svgElement = document.getElementById(`logo-${name}`);
    if (!svgElement) return;
    
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgData], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement("a");
    link.href = url;
    link.download = `global-metafora-logo-${name}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Logo ${name} descargado en SVG`);
  };

  const downloadPNG = async (size, name) => {
    const svgElement = document.getElementById(`logo-${name}`);
    if (!svgElement) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = size * 2;
    canvas.height = size * 2;

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = "transparent";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        const pngUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = pngUrl;
        link.download = `global-metafora-logo-${name}-${size * 2}px.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(pngUrl);
        toast.success(`Logo ${name} descargado en PNG (${size * 2}px)`);
      }, "image/png");
      
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  const copySVGCode = () => {
    const svgElement = document.getElementById("logo-full");
    if (!svgElement) return;
    
    const svgData = new XMLSerializer().serializeToString(svgElement);
    navigator.clipboard.writeText(svgData);
    setCopied(true);
    toast.success("Código SVG copiado al portapapeles");
    setTimeout(() => setCopied(false), 2000);
  };

  const logoVariants = [
    { name: "full", label: "Logo Completo", size: 400, component: GlobalMetaforaLogo },
    { name: "icon", label: "Solo Icono", size: 200, component: LogoIcon },
  ];

  return (
    <div className="min-h-screen bg-[#02040a] py-20">
      <div className="section-container">
        {/* Header */}
        <div className="mb-12">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#94a3b8] hover:text-white mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al sitio
          </Link>
          <h1 className="text-4xl font-bold text-white mb-4">
            Descargar Logo - GLOBAL METAFORA APP
          </h1>
          <p className="text-[#94a3b8] text-lg">
            Descarga el logo en diferentes formatos para usar en redes sociales, tarjetas de presentación, documentos y más.
          </p>
        </div>

        {/* Logo Full Preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-12 mb-8"
        >
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Logo Principal</h2>
          
          <div className="flex flex-col items-center gap-8">
            {/* Dark background preview */}
            <div className="bg-[#02040a] p-12 rounded-lg border border-white/10">
              <div id="logo-full">
                <GlobalMetaforaLogo size={300} />
              </div>
            </div>

            {/* Logo with text */}
            <div className="text-center">
              <h2 className="text-3xl font-bold text-white tracking-wider mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                GLOBAL <span className="text-[#3b82f6]">METAFORA</span>
              </h2>
              <p className="text-[#06b6d4] font-semibold tracking-[0.3em] text-xl">APP</p>
            </div>

            {/* Download buttons */}
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => downloadSVG(300, "full")}
                className="btn-primary flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                <span>Descargar SVG</span>
              </button>
              <button
                onClick={() => downloadPNG(300, "full")}
                className="btn-secondary flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                <span>Descargar PNG (600px)</span>
              </button>
              <button
                onClick={() => downloadPNG(500, "full")}
                className="btn-secondary flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                <span>Descargar PNG (1000px)</span>
              </button>
              <button
                onClick={copySVGCode}
                className="btn-secondary flex items-center gap-2"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                <span>{copied ? "¡Copiado!" : "Copiar código SVG"}</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Icon Version */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-12 mb-8"
        >
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Icono (Para Redes Sociales)</h2>
          
          <div className="flex flex-col items-center gap-8">
            <div className="flex gap-8">
              {/* Dark background */}
              <div className="text-center">
                <p className="text-[#94a3b8] text-sm mb-4">Fondo oscuro</p>
                <div className="bg-[#02040a] p-8 rounded-lg border border-white/10">
                  <div id="logo-icon">
                    <LogoIcon size={150} />
                  </div>
                </div>
              </div>

              {/* Light background */}
              <div className="text-center">
                <p className="text-[#94a3b8] text-sm mb-4">Fondo claro</p>
                <div className="bg-white p-8 rounded-lg">
                  <LogoIcon size={150} />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => downloadSVG(150, "icon")}
                className="btn-primary flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                <span>Descargar SVG</span>
              </button>
              <button
                onClick={() => downloadPNG(150, "icon")}
                className="btn-secondary flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                <span>PNG 300px</span>
              </button>
              <button
                onClick={() => downloadPNG(256, "icon")}
                className="btn-secondary flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                <span>PNG 512px</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Size variations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card p-12 mb-8"
        >
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Variaciones de Tamaño</h2>
          
          <div className="flex flex-wrap justify-center items-end gap-8">
            {[32, 48, 64, 96, 128].map((size) => (
              <div key={size} className="text-center">
                <div className="bg-[#0b1221] p-4 rounded-lg mb-2 flex items-center justify-center" style={{ minWidth: size + 32, minHeight: size + 32 }}>
                  <LogoIcon size={size} />
                </div>
                <p className="text-[#94a3b8] text-xs">{size}px</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Brand Colors */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card p-12"
        >
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Colores de Marca</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { name: "Azul Principal", hex: "#3b82f6", rgb: "59, 130, 246" },
              { name: "Azul Claro", hex: "#60a5fa", rgb: "96, 165, 250" },
              { name: "Cyan", hex: "#06b6d4", rgb: "6, 182, 212" },
              { name: "Fondo", hex: "#02040a", rgb: "2, 4, 10" },
            ].map((color) => (
              <div key={color.hex} className="text-center">
                <div 
                  className="w-full h-24 rounded-lg mb-3 border border-white/10"
                  style={{ backgroundColor: color.hex }}
                />
                <p className="text-white font-medium">{color.name}</p>
                <p className="text-[#94a3b8] text-sm">{color.hex}</p>
                <p className="text-[#94a3b8] text-xs">RGB: {color.rgb}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Usage Guidelines */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 p-6 border border-[#3b82f6]/30 rounded-lg"
        >
          <h3 className="text-xl font-bold text-white mb-4">Guía de Uso</h3>
          <ul className="space-y-2 text-[#94a3b8]">
            <li>• Usa el logo completo para materiales de marketing y presentaciones</li>
            <li>• Usa el icono para redes sociales, favicon y espacios pequeños</li>
            <li>• Mantén los colores originales - no modifiques los gradientes</li>
            <li>• Deja suficiente espacio alrededor del logo</li>
            <li>• El logo funciona mejor sobre fondos oscuros</li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
};

export default LogoDownloadPage;
